import DOMPurify from 'dompurify';

/**
 * Encode a string to base64, handling non-ASCII (Unicode) characters safely.
 * @param str - The string to encode
 * @returns Base64-encoded string
 */
function utf8ToBase64(str: string): string {
  return btoa(
    encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) =>
      String.fromCharCode(parseInt(p1, 16)),
    ),
  );
}

/**
 * Decode a base64 string that was encoded with utf8ToBase64.
 * Handles non-ASCII (Unicode) characters safely.
 * @param str - The base64-encoded string
 * @returns Decoded UTF-8 string
 */
function base64ToUtf8(str: string): string {
  return decodeURIComponent(
    atob(str)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join(''),
  );
}

type OAuthStateResult = {
  nonce: string;
  encodedState: string;
};

/**
 * Build an OAuth state parameter that includes both the security nonce
 * and optional metadata (e.g., utm_source).
 * The state is base64-encoded JSON to safely pass through the OAuth flow.
 * @param metadata - Optional metadata to include in the state
 * @returns Object containing the nonce and encodedState
 */
export function buildOAuthState(
  metadata: Record<string, unknown> = {},
): OAuthStateResult {
  const nonce = crypto.randomUUID();
  const statePayload = { ...metadata, nonce };
  const encodedState = utf8ToBase64(JSON.stringify(statePayload));
  return { nonce, encodedState };
}

/**
 * Extract the nonce from an OAuth state parameter.
 * Handles both legacy (plain UUID) and new (base64-encoded JSON) formats.
 * @param state - The OAuth state parameter from the callback
 * @returns The nonce value, or null if extraction fails
 */
export function extractNonceFromOAuthState(
  state: string | null | undefined,
): string | null {
  if (!state) return null;

  // Check if it's the legacy plain UUID format (36 characters, matches UUID pattern)
  const uuidPattern =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (uuidPattern.test(state)) {
    return state;
  }

  // Try to decode as base64 JSON (handles both ASCII and Unicode content)
  try {
    const decoded = JSON.parse(base64ToUtf8(state));
    return decoded.nonce || null;
  } catch {
    // If decoding fails, return the state as-is (backwards compatibility)
    return state;
  }
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Build a case-insensitive, global RegExp that matches a company name within
 * a sentence, tolerating extra whitespace and an optional trailing "LLC"/"Inc".
 * Example: for "DC Comics" it matches "dccomics", "dc comics", and "d c comics".
 * Regex metacharacters in the name are escaped.
 */
function buildCompanyNameRegex(name: string): RegExp {
  const nameWithoutLLCOrInc = name
    .replace(/(,?\s+(LLC|Inc)\.?)$|/gi, '')
    .replace(/\s+/gi, '');

  return new RegExp(
    `${nameWithoutLLCOrInc
      .split('')
      .map((char) => {
        if (char === ',') return '\\s*,?\\s*'; // replace comma with optional comma and white spaces
        if (char === '.') return '\\s*\\.?\\s*'; // replace dot with optional dot and white spaces
        return escapeRegExp(char);
      })
      .join('(\\.\\s*|\\s*)')}(\\s*,?\\s+(LLC|Inc)\\.?\\s*?)?`, // match LLC or Inc at the end, we're replacing with the name so we want that as optional
    'gi',
  );
}

/**
 * Truncate a sentence, wrap it in quotes, highlight occurrences of the given
 * company name with a <strong class="highlight-me"> tag, and sanitize the
 * result so it is safe to render as HTML.
 * @param sentence - The sentence to render
 * @param name - The company name to highlight
 * @returns Sanitized HTML string
 */
export function highlightCompanyNameInSentence(
  sentence: string,
  name: string,
): string {
  const maxSentenceLength = 300;

  const truncatedSentence =
    sentence.length > maxSentenceLength
      ? `"${sentence.substring(0, maxSentenceLength)}..."`
      : `"${sentence}"`;

  return DOMPurify.sanitize(
    truncatedSentence.replace(
      buildCompanyNameRegex(name),
      () => `<strong class="highlight-me">${name}</strong>`,
    ),
  );
}
